import React from "react";
import { useTranslation } from "react-i18next";
import {
    StyledModal,
    StyledH2,
    StyledLabel,
    CheckboxIcon,
} from "@aureskonnect/react-ui";
import { ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { capitalize } from "@helpers/general";
import { toast } from "react-toastify";
import { useSnapshot } from "valtio";

import { store } from "@components/VerticalLayout/store";
import { useFetchGlobal } from "../../../../api/hooks";
import {
    store as InformationStore,
    setIsAssociateModalOpened,
    setIsAssociateModalClosed,
    setNumberActiveMultiShop,
    setIsLoading,
    setKey,
} from "../store";

import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";
import { CustomSecondaryColorButton } from "@components/Common/CustomSecondaryColorButton";
import { setItemsDisable } from "@store";
import { checkIfAssociated, groupByShopId } from "../helper";
import { uuid } from "uuidv4";

type ConfirmationAssociatedMessageType = {
    isModalOpened: boolean;
    setIsModalOpened: Function;
};

export function ConfirmationAssociatedMessage({
    isModalOpened,
    setIsModalOpened,
}: ConfirmationAssociatedMessageType) {
    const { t } = useTranslation();
    const { fetchGlobal } = useFetchGlobal();
    const { franchiseID, userID, operatorID } = useSnapshot(store);
    const {
        localShop,
        isAssociateModalOpened,
        isAssociateModalClosed,
        numberActiveMultiShop,
        selectedRows,
        ids,
        rowsShop,
        activeModeInformation,
        message,
    } = useSnapshot(InformationStore);

    const uuidUser = localStorage.getItem("uuidUser");

    async function AssociateModeOfInformation() {
        if (selectedRows.length === 0 || localShop.length === 0) return;

        setIsLoading(true);
        toast.dismiss();

        let isReassignment = false;
        const shopIds = [...new Set(localShop)];

        const activeModeMap = new Map(
            activeModeInformation.map((item: any) => [item.Id, item])
        );

        const validRows = selectedRows.filter((el: any) => el.Id !== undefined);

        const localData = localShop.map((shopElement) => {
            const associated = checkIfAssociated(shopElement, rowsShop, t);

            const associatedMap = new Map(
                associated.map((item: any) => [item.Id, item])
            );

            const objectData: Record<string, any> = {};

            for (const el of validRows) {
                const activeMode = activeModeMap.get(el.Id);
                if (!activeMode) continue;

                const associatedItem = associatedMap.get(el.Id);
                const isAlreadyAssociated = !!associatedItem;

                if (
                    isAlreadyAssociated &&
                    activeMode.state === associatedItem.state
                ) {
                    continue;
                }

                if (isAlreadyAssociated) isReassignment = true;

                objectData[el.Id] = {
                    designation: el[t("Mode of information")],
                    idMI: el?.idMI,
                    supportOfSale: el[t("Sales support")],
                    salesSupportData: [el[t("Sales support")]],
                    displayName: el[t("Display name")],
                    modeOfSale: el[t("Sales method")],
                    tag: el.tag,
                    isArchived: el.isArchived,
                    shopId: shopElement,
                    state: activeMode.state,
                    isReassignment: isAlreadyAssociated,
                    advancedDisplayName: el.advancedDisplayName,
                };
            }

            return {
                userId: userID,
                operatorId: operatorID,
                uuidUser: uuidUser,
                shopId: shopIds,
                franchiseId: franchiseID,
                data: objectData,
                isShop: true,
                isAssociate: true,
            };
        });

        if (localData.length === 0) return;

        const groupedData = groupByShopId(localData);

        try {
            const response: any = await fetchGlobal(
                `${process.env.REACT_APP_API_V2_URL}/settings/general/mode_of_Information`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(groupedData),
                }
            );

            if (response.error === false) {
                const itemsPromise = fetchGlobal(
                    `${process.env.REACT_APP_API_V2_URL}/settings/consomation/items?franchiseId=${franchiseID}&shopId=0`,
                    {
                        method: "GET",
                        headers: { "Content-Type": "application/json" },
                    }
                );

                toast.success(
                    isReassignment
                        ? t("Information mode reassignment was successful")
                        : t("Information mode assignment was successful")!,
                    {
                        position: toast.POSITION.TOP_CENTER,
                        theme: "colored",
                        icon: (
                            <CheckboxIcon height={25} width={25} fill="white" />
                        ),
                    }
                );

                setKey(uuid());
                setNumberActiveMultiShop(
                    numberActiveMultiShop +
                        selectedRows.length * localShop.length -
                        ids
                );

                const itemsDisable: any = await itemsPromise;
                if (itemsDisable.error === false) {
                    setItemsDisable(itemsDisable.data);
                }
            }

            if (response.blockAssign === true) {
                toast.warn(
                    t(
                        "Please note that the assignment could not be made to one or more stores. Please check the store(s) data settings."
                    ),
                    {
                        position: toast.POSITION.TOP_CENTER,
                        autoClose: 2000,
                        theme: "colored",
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                    }
                );
            }
        } catch (e) {
            toast.error(`${t("There's an error")!}`, {
                position: toast.POSITION.TOP_CENTER,
                autoClose: 2000,
                theme: "colored",
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <StyledModal toggle={true} isOpen={isModalOpened!} centered>
            <ModalHeader
                className="text-capitalize"
                toggle={() => setIsModalOpened(!isModalOpened)}
            >
                <StyledH2>{capitalize(t("Alert"))}</StyledH2>
            </ModalHeader>
            <ModalBody>
                <StyledLabel
                    className="pl-3 m-0"
                    style={{ whiteSpace: "pre-line" }}
                >
                    {message}
                </StyledLabel>
            </ModalBody>
            <ModalFooter className="border-top-0 ">
                <CustomSecondaryColorButton
                    outline
                    variant="light"
                    onClick={() => {
                        setIsModalOpened(!isModalOpened);
                    }}
                    rounded
                >
                    {t("Cancel")}
                </CustomSecondaryColorButton>
                <CustomMainColorButton
                    rounded
                    className="mr-2"
                    variant="primary"
                    onClick={() => {
                        AssociateModeOfInformation();
                        setIsModalOpened(!isModalOpened);
                        setIsAssociateModalOpened(!isAssociateModalOpened);
                        setIsAssociateModalClosed(!isAssociateModalClosed);
                    }}
                >
                    {t("Validate")}
                </CustomMainColorButton>
            </ModalFooter>
        </StyledModal>
    );
}
